import { Col, Empty, Row, Typography } from "antd";
import { type ReactNode } from "react";

const { Text } = Typography;

export type RecipeDetailIngredientItem = {
  id: string;
  imageUrl?: string | null;
  name: string;
  quantity: number;
  unit: string;
  unitName?: string | null;
};

export type RecipeDetailStep = {
  id: string;
  stepNumber?: number | null;
  description: string;
  ingredients?: RecipeDetailIngredientItem[] | null;
};

type FormatQuantity<TItem extends RecipeDetailIngredientItem> = (item: TItem) => string;

type SharedSectionAppearance = {
  borderColor?: string;
  textColor?: string;
};

type InstructionsAppearance = SharedSectionAppearance & {
  badgeBackground?: string;
  badgeColor?: string;
};

export type RecipeInstructionsSectionProps = {
  steps: RecipeDetailStep[];
  formatQuantity: FormatQuantity<RecipeDetailIngredientItem>;
  emptyDescription?: string;
  appearance?: InstructionsAppearance;
};

export type RecipeIngredientsSectionProps<TItem extends RecipeDetailIngredientItem = RecipeDetailIngredientItem> = {
  items: TItem[];
  formatQuantity: FormatQuantity<TItem>;
  emptyDescription?: string;
  appearance?: SharedSectionAppearance;
  renderSupplementaryContent?: (item: TItem) => ReactNode;
};

export type RecipeStepIngredientGridProps = {
  items: RecipeDetailIngredientItem[];
  formatQuantity: FormatQuantity<RecipeDetailIngredientItem>;
  textColor?: string;
};

function renderEmptyState(description: string) {
  return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={description} style={{ marginBlock: 24 }} />;
}

function getIngredientImageUrl(imageUrl: string) {
  return `${imageUrl}/small`;
}

function IngredientIdentityRow({
  item,
  quantity,
  textColor,
  imageSize = 24,
}: {
  item: RecipeDetailIngredientItem;
  quantity: string;
  textColor?: string;
  imageSize?: number;
}) {
  return (
    <div
      data-testid="recipe-ingredient-identity"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        minWidth: 0,
      }}
    >
      {item.imageUrl ? (
        <img
          src={getIngredientImageUrl(item.imageUrl)}
          loading="lazy"
          decoding="async"
          alt={item.name !== "" ? item.name : "Ingredient Image"}
          width={imageSize}
          height={imageSize}
          style={{ flex: "0 0 auto", borderRadius: 4, objectFit: "cover" }}
        />
      ) : null}
      <Text
        style={{
          minWidth: 0,
          flex: 1,
          color: textColor,
        }}
        ellipsis={{ tooltip: item.name }}
      >
        {item.name}
      </Text>
      <Text
        strong
        style={{
          flex: "0 0 auto",
          whiteSpace: "nowrap",
          color: textColor,
        }}
      >
        {quantity}
      </Text>
    </div>
  );
}

export function RecipeStepIngredientGrid({
  items,
  formatQuantity,
  textColor,
}: RecipeStepIngredientGridProps) {
  return (
    <Row gutter={[12, 4]} data-testid="recipe-step-ingredient-grid">
      {items.map((item) => (
        <Col key={item.id} xs={24} sm={12} md={12} lg={8} xl={8} xxl={6}>
          <div
            data-testid="recipe-step-ingredient-cell"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              minHeight: 24,
            }}
          >
            <IngredientIdentityRow
              item={item}
              quantity={formatQuantity(item)}
              textColor={textColor}
              imageSize={24}
            />
          </div>
        </Col>
      ))}
    </Row>
  );
}

export function RecipeInstructionsSection({
  steps,
  formatQuantity,
  emptyDescription = "No steps",
  appearance,
}: RecipeInstructionsSectionProps) {
  if (steps.length === 0) {
    return renderEmptyState(emptyDescription);
  }

  return (
    <div>
      {steps.map((step, index) => (
        <div
          key={step.id}
          style={{
            display: "grid",
            gridTemplateColumns: "34px 1fr",
            gap: 14,
            padding: "16px 0",
            borderBottom: index === steps.length - 1 ? "none" : `1px solid ${appearance?.borderColor ?? "#E0DED4"}`,
          }}
        >
          <span
            style={{
              width: 28,
              height: 28,
              borderRadius: 999,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              background: appearance?.badgeBackground ?? "#4F7B58",
              color: appearance?.badgeColor ?? "#fff",
              fontWeight: 700,
            }}
          >
            {step.stepNumber || index + 1}
          </span>
          <div>
            <Text style={{ lineHeight: 1.6, color: appearance?.textColor }}>{step.description}</Text>
            {step.ingredients && step.ingredients.length > 0 ? (
              <div style={{ marginTop: 8 }}>
                <RecipeStepIngredientGrid
                  items={step.ingredients}
                  formatQuantity={formatQuantity}
                  textColor={appearance?.textColor}
                />
              </div>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}

export function RecipeIngredientsSection<TItem extends RecipeDetailIngredientItem>({
  items,
  formatQuantity,
  emptyDescription = "No ingredients defined",
  appearance,
  renderSupplementaryContent,
}: RecipeIngredientsSectionProps<TItem>) {
  if (items.length === 0) {
    return renderEmptyState(emptyDescription);
  }

  return (
    <div>
      {items.map((item, index) => {
        const extraContent = renderSupplementaryContent?.(item);

        return (
          <div
            key={item.id}
            data-testid="recipe-ingredient-row"
            style={{
              padding: "12px 0",
              borderBottom: index === items.length - 1 ? "none" : `1px solid ${appearance?.borderColor ?? "#E0DED4"}`,
            }}
          >
            <IngredientIdentityRow
              item={item}
              quantity={formatQuantity(item)}
              textColor={appearance?.textColor}
            />
            {extraContent ? <div style={{ marginTop: 10 }}>{extraContent}</div> : null}
          </div>
        );
      })}
    </div>
  );
}
